<template>
  <div id="product-mobile-footer-bar" ref="footerBarRef" class="transition-all w-full fixed bottom-0 left-0">
    <mobile-footer-price-bar
      :action-text="actionBtnText"
      :is-loading="isReservationNextStepLoading"
      :is-submittable="isSubmittable"
      :button-class="isShowBookingInfo ? 'w-auto' : 'w-full'"
      @submit="handleActionBtnClick"
    >
      <p v-show="isShowBookingInfo" class="booking-info font-500 text-base">
        <template v-if="!isSoldOut">
          <span class="text-sm">{{ bookingAttendanceString }}</span>
          <br />
          <span class="text-base">{{ bookingDateTimeDiscountString }}</span>
        </template>
      </p>
    </mobile-footer-price-bar>
  </div>
</template>

<script setup lang="ts">
import MobileFooterPriceBar from '@/components/common/mobile-footer-price-bar.vue'

enum BottomBarAction {
  BOOK_NOW = 'book_now',
  NEXT = 'next',
  SOLD_OUT = 'sold_out',
}

const { t } = useI18n()

const SIMILAR_CATEGORIES_ID = 'product-detail-section-similar-categories'
const BOOKING_SECTION_ID = 'product-detail-section-name'
const ACTION_BUTTON_TEXT_MAP = {
  [BottomBarAction.BOOK_NOW]: t('branch_detail.bottom_bar.book_now'),
  [BottomBarAction.NEXT]: t('branch_detail.bottom_bar.next'),
  [BottomBarAction.SOLD_OUT]: t('branch_detail.bottom_bar.sold_out'),
}

const { branch, attendance, date, slotInfo, isSoldOut } = useBranchDetailContext()
const { format } = useI18nDateFns()
const { formatDateWithI18n } = useI18nDateString()
const { isReservationNextStepLoading, processReservationNextStep } = useReservationNextStep({ branch, attendance, date, slotInfo, format })

const footerBarRef = ref<Nullable<HTMLElement>>(null)
const isBookingSectionVisible = ref(true)

const hasSelectedSlot = computed(() => !!slotInfo.value)

const currentAction = computed(() => {
  if (isSoldOut.value) return BottomBarAction.SOLD_OUT
  if (isBookingSectionVisible.value || hasSelectedSlot.value) return BottomBarAction.NEXT
  return BottomBarAction.BOOK_NOW
})

const isSubmittable = computed(() => !isSoldOut.value && (!isBookingSectionVisible.value || hasSelectedSlot.value))

const isShowBookingInfo = computed(() => currentAction.value !== BottomBarAction.BOOK_NOW)

const actionBtnText = computed(() => ACTION_BUTTON_TEXT_MAP[currentAction.value])

const bookingAttendanceString = computed(() => {
  return isSoldOut.value ? '' : t('branch_detail.bottom_bar.people', { pax: attendance.value }, attendance.value)
})

const bookingDateString = computed(() => (isSoldOut.value || !date.value ? '' : formatDateWithI18n(date.value, 'MMMD')))

const bookingTimeString = computed(() =>
  slotInfo.value?.slot
    ? formatDateWithI18n(slotInfo.value.slot, {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      })
    : '--:--'
)

const discountString = computed(() =>
  slotInfo.value?.discount ? t('branch_detail.bottom_bar.discount', { ratio: slotInfo.value.discount }, slotInfo.value.discount) : '--%'
)

const bookingDateTimeDiscountString = computed(() => {
  return isSoldOut.value ? '' : `${bookingDateString.value}, ${bookingTimeString.value} / ${discountString.value}`
})

const nextStep = () => {
  if (!isSubmittable.value) return

  processReservationNextStep()
}

const handleActionBtnClick = () => {
  switch (currentAction.value) {
    case BottomBarAction.BOOK_NOW:
      scrollToId(BOOKING_SECTION_ID, false)
      break
    case BottomBarAction.NEXT:
      nextStep()
      break
  }
}

onMounted(() => {
  const sectionSimilarCategories = document.getElementById(SIMILAR_CATEGORIES_ID)
  if (sectionSimilarCategories && footerBarRef.value) {
    useIntersectionObserver(
      sectionSimilarCategories,
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            footerBarRef.value?.classList.add('opacity-0', '-bottom-72px')
          } else {
            footerBarRef.value?.classList.remove('opacity-0', '-bottom-72px')
          }
        })
      },
      { threshold: 1 }
    )
  }

  const bookingSection = document.getElementById(BOOKING_SECTION_ID)
  if (bookingSection && footerBarRef.value && !isSoldOut.value) {
    useIntersectionObserver(
      bookingSection,
      entries => {
        entries.forEach(entry => {
          isBookingSectionVisible.value = entry.isIntersecting
        })
      },
      { threshold: [0, 1], rootMargin: '-128px 0px 0px 0px' }
    )
  }
})
</script>
